import {
  memo,
  useRef,
  useMemo,
  useEffect,
  useCallback,
  RefObject,
} from 'react';
import {
  Stage,
  Layer,
  Rect as KonvaRect,
  Image as KonvaImage,
} from 'react-konva';
import { useTheme } from 'react-jss';
import {
  toCanvas,
  drawNode,
  addToNode,
  cacheNode,
  cloneNode,
  createLayer,
  setNodeAttrs,
  getNodeLayer,
  clearNodeCache,
  setNodeGlobalCompositeOperation,
} from '@picsart/web-layering/rendering/konva/selectors';
import { Image, Stage as StageType, CanvasLayer as LayerType } from '@picsart/web-layering/types';

import { ITheme } from 'types';
import { WATERMARK } from 'configs/constants';
import useWatermarkImage from 'hooks/useWatermarkImage';

import removeScrollLayersFromCanvas from './removeScrollLayersFromCanvas';
import addScrollLayersToCanvas from './addScrollLayersToCanvas';
import EraserLine from './EraserLine';
import { useStageStyles } from './styles';
import { SettingDataType } from './EraserSettings';
import { EraserToolsEnum, IEraserImageAttrs } from './config';
import { getWatermarkAttrs } from './utils';
import { batchDrawNode } from './selectors';

interface IEraserStageProps {
  width: number;
  height: number;
  scale: number;
  onChange: () => void;
  showWatermark: boolean;
  settings: SettingDataType;
  activeTool: EraserToolsEnum;
  actionsIsBlock: boolean;
  originalImageAttrs?: IEraserImageAttrs;
  effectImage: HTMLImageElement | undefined;
  originalImage: HTMLImageElement | undefined;
  onToolSelect: (type: EraserToolsEnum) => void;
  mask: HTMLImageElement | HTMLCanvasElement | undefined;
  onMaskChange: (mask: HTMLImageElement | HTMLCanvasElement) => void;
  containerRef: RefObject<HTMLDivElement>;
  maxWidthCanvas: number;
}

const EraserStage = ({
  mask,
  width,
  height,
  scale,
  onChange,
  settings,
  activeTool,
  effectImage,
  onMaskChange,
  onToolSelect,
  showWatermark,
  actionsIsBlock,
  originalImage,
  originalImageAttrs,
  containerRef,
  maxWidthCanvas,
}: IEraserStageProps) => {
  const theme = useTheme<ITheme>();
  const classes = useStageStyles();
  const wmImage = useWatermarkImage({
    width,
    height,
  });

  const maskRef = useRef<Image>(null);
  const effectImageRef = useRef<Image | null>(null);
  const stageRef = useRef<StageType | null>(null);
  const layerRef = useRef<LayerType[]>([]);

  const isErasing = useMemo(() => activeTool === EraserToolsEnum.ERASER, [activeTool]);

  const pixelRatio = useMemo(
    () => (effectImage ? effectImage.width / width : 1),
    [effectImage, width],
  );

  useEffect(() => {
    if (effectImage && effectImageRef.current) {
      batchDrawNode(getNodeLayer(cacheNode(effectImageRef.current)));
    }
  }, [effectImage, width, height]);

  useEffect(() => {
    if (mask && maskRef.current) {
      batchDrawNode(getNodeLayer(cacheNode(maskRef.current)));
    }
  }, [mask, originalImageAttrs, width, height]);

  const handleEffectRef = useCallback(ref => {
    effectImageRef.current = ref;
    if (ref) {
      batchDrawNode(getNodeLayer(cacheNode(ref)));
    }
  }, []);

  const updateMask = useCallback(async (lineRef, cacheOffset) => {
    const layer = createLayer();
    const line = cloneNode(lineRef);
    setNodeGlobalCompositeOperation(line, isErasing ? '' : 'destination-out');
    cacheOffset && cacheNode(line, { offset: cacheOffset });

    const maskClone = maskRef.current && cloneNode(maskRef.current);
    setNodeAttrs(maskClone, { opacity: 1 });
    setNodeGlobalCompositeOperation(maskClone, '');

    addToNode(layer, maskClone);
    addToNode(layer, line);

    const newImage = toCanvas(layer, {
      pixelRatio,
      width,
      height,
      ...originalImageAttrs,
    });
    onMaskChange(newImage);

    clearNodeCache(lineRef);
  }, [width, height, isErasing, pixelRatio, originalImageAttrs, onMaskChange]);

  const applyLine = useCallback(async (lineRef, cacheOffset) => {
    await updateMask(lineRef, cacheOffset);
    onChange();
  }, [onChange, updateMask]);

  const handleActiveEraserTool = useCallback(() => {
    activeTool === EraserToolsEnum.CUT_OUT && onToolSelect(EraserToolsEnum.ERASER);
  }, [activeTool, onToolSelect]);

  const watermarkAttrs = useMemo(
    () => wmImage && getWatermarkAttrs(wmImage, width, height),
    [height, width, wmImage],
  );

  useEffect(() => {
    if (!stageRef.current || !containerRef.current || !Object.keys(layerRef.current).length) {
      return;
    }

    removeScrollLayersFromCanvas(stageRef.current);

    drawNode(stageRef.current);
    if (scale <= 1) {
      return;
    }

    const { offsetHeight } = containerRef.current;
    const layersArr = layerRef.current;

    addScrollLayersToCanvas({
      stage: stageRef.current,
      layers: layersArr,
      scale,
      currentContainerWidth: maxWidthCanvas,
      currentContainerHeight: offsetHeight,
    });
  }, [scale, containerRef, width, maxWidthCanvas]);

  const addLayerRef = (ref: LayerType) => {
    !layerRef.current.includes(ref) && layerRef.current.push(ref);
  };

  return (
    <Stage
        // @ts-ignore
      ref={stageRef}
      className={classes.stage}
      width={width * scale}
      height={height * scale}
      scaleX={scale}
      scaleY={scale}
    >
      <Layer
        listening={false}
          // @ts-ignore
        ref={ref => ref && addLayerRef(ref)}
      >
        {/* @ts-ignore */}
        <KonvaRect
          width={width}
          height={height}
          {...originalImageAttrs}
          fillPatternImage={originalImage}
          fillPatternRepeat="repeat"
        />
      </Layer>

      <Layer
        listening={false}
          // @ts-ignore
        ref={ref => ref && addLayerRef(ref)}
      >
        <KonvaImage
            // @ts-ignore
          ref={maskRef}
          id="mask-img"
          width={width}
          height={height}
          image={mask}
          {...originalImageAttrs}
        />
        {isErasing && (
        <KonvaImage
          ref={handleEffectRef}
          image={effectImage}
          width={width}
          height={height}
          globalCompositeOperation="source-out"
        />
        )}
        <EraserLine
          firstCircleColor={theme.getColor('--absolute-black-base')}
          secondCircleColor={theme.getColor('--absolute-white-base')}
          settings={settings}
          onChange={applyLine}
          containerRef={containerRef}
          onAction={handleActiveEraserTool}
          scale={scale}
          actionsIsBlock={actionsIsBlock}
        >
          {!isErasing && (
          <KonvaImage
            ref={handleEffectRef}
            image={effectImage}
            width={width}
            height={height}
            globalCompositeOperation="source-out"
          />
          )}
        </EraserLine>
      </Layer>

      {showWatermark && wmImage && (
      <Layer
            // @ts-ignore
        ref={ref => ref && addLayerRef(ref)}
      >
        <KonvaImage
          id={WATERMARK}
          image={wmImage}
          {...watermarkAttrs}
        />
      </Layer>
      )}
    </Stage>
  );
};

export default memo(EraserStage);
